import { toast } from "sonner";
import { Wrench, FileText, Sparkles, ShoppingBag } from "lucide-react";
import { MECHANIC_SERVICES } from "@/data/mechanicPricing";
import { useCart } from "@/contexts/CartContext";
import { useContent } from "@/contexts/ContentContext";

const MECHANIC_IMG = "https://images.unsplash.com/photo-1775285157647-73f29f910216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxjYXIlMjBlbmdpbmUlMjBtZWNoYW5pY3xlbnwwfHx8fDE3NzgxNDQ1ODF8MA&ixlib=rb-4.1.0&q=85";

export default function Mechanic({ hideHeader = false }) {
  const { addItem } = useCart();
  const { content } = useContent();

  const items = [
    ...MECHANIC_SERVICES.map((s) => ({ ...s, image: content.images?.[s.id] || null })),
    ...(content.custom?.mecanique || []).map((s) => ({ ...s, custom: true, image: content.images?.[s.id] || s.image || null })),
  ];

  const handleAdd = (service) => {
    const price = Number(service.price) > 0 ? Number(service.price) : 0;
    addItem({
      id: `meca_${service.id}`,
      type: "mecanique",
      label: service.name,
      price,
      quantity: 1,
      meta: price > 0 ? null : "Sur devis — précisez votre véhicule via WhatsApp",
    });
    toast.success(`${service.name} ajouté au panier${price > 0 ? "" : " (sur devis)"}`);
  };

  return (
    <section id="mecanique" className="relative py-20 lg:py-28 border-t border-white/5 overflow-hidden" data-testid="mechanic-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-12">
          {/* Image */}
          <div className="lg:col-span-5 relative">
            <div className="absolute inset-0 bg-[#E50914]/20 rounded-xl blur-3xl"></div>
            <div className="relative rounded-xl overflow-hidden border border-white/10 aspect-[4/3] lg:aspect-[4/5]">
              <img src={MECHANIC_IMG} alt="Mécanique automobile" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-black/60 backdrop-blur-sm border border-[#E50914]/40 rounded-full mb-3">
                  <Wrench className="w-3 h-3 text-[#E50914]" />
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-white">Atelier mobile</span>
                </div>
                <div className="text-white font-bold text-xl">Devis personnalisés</div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-7">
            {!hideHeader && (
              <>
                <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">/ Petite mécanique</div>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6">
                  Services de<br />
                  <span className="text-gradient-red">petites mécaniques.</span>
                </h2>
              </>
            )}
            <p className="text-white/60 text-lg leading-relaxed mb-6 max-w-xl">
              Chaque véhicule est unique : nous établissons un devis sur-mesure selon votre modèle, motorisation et l'état des pièces. Ajoutez les services qui vous intéressent au panier — le tarif est confirmé par WhatsApp.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E50914]/10 border border-[#E50914]/40 rounded-lg">
                <FileText className="w-4 h-4 text-[#E50914]" />
                <span className="text-sm font-bold uppercase tracking-wider text-white">Devis gratuit & sans engagement</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
                <Sparkles className="w-4 h-4 text-[#E50914]" />
                <span className="text-sm font-medium text-white/80">Réponse sous 24h</span>
              </div>
            </div>
          </div>
        </div>

        {/* Service cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((s) => (
            <ServiceCard key={s.id} service={s} onAdd={handleAdd} />
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-white/40">
          Ajoutez vos services au panier, puis envoyez votre demande via WhatsApp en précisant votre véhicule (plaque ou modèle).
        </p>
      </div>
    </section>
  );
}

function ServiceCard({ service, onAdd }) {
  const Icon = service.icon || Wrench;
  const price = Number(service.price) > 0 ? Number(service.price) : 0;
  return (
    <div
      data-testid={`mech-card-${service.id}`}
      className="group relative text-left bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl hover:border-[#E50914]/40 hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
    >
      {service.image ? (
        <div className="relative h-36 overflow-hidden bg-[#0E0E0E]">
          <div className="absolute inset-0 flex items-center justify-center z-0"><Icon className="w-9 h-9 text-white/15" /></div>
          <img src={service.image} alt={service.name} loading="lazy"
            className="relative z-10 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
        </div>
      ) : (
        <div className="relative w-12 h-12 mt-6 ml-6 rounded-lg bg-gradient-to-br from-[#E50914]/20 to-transparent border border-[#E50914]/30 flex items-center justify-center group-hover:scale-110 transition-transform">
          <Icon className="w-5 h-5 text-[#E50914]" strokeWidth={2} />
        </div>
      )}

      <div className="p-6 flex flex-col flex-1">
        <h3 className="relative font-bold text-white text-lg mb-1">{service.name}</h3>
        <p className="relative text-xs text-white/50 mb-5 leading-relaxed flex-1">{service.desc}</p>

        <div className="relative pt-4 border-t border-white/10 flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/40">Tarif</div>
            <div className="text-sm font-bold text-white">{price > 0 ? `${price}€` : "Sur devis"}</div>
          </div>
        </div>

        <button type="button" onClick={() => onAdd(service)} data-testid={`mech-add-${service.id}`}
          className="relative w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-xs tracking-wider px-4 py-3 rounded-lg border border-[#E50914] hover:shadow-[0_0_20px_rgba(229,9,20,0.4)] transition-all"
        >
          <ShoppingBag className="w-4 h-4" /> Ajouter au panier
        </button>
      </div>
    </div>
  );
}
